import { searchEmoji } from "@wiki/core";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { cx } from "../../lib/cx";
import { insertEmoji, insertMention } from "../commands/media";
import type { EditorContext } from "../context";
import {
  activeSuggestion,
  dismissSuggestion,
  replaceSuggestion,
  type SuggestionState,
} from "../plugins/suggestions";
import type { MentionSearchResult } from "../types";
import { MenuPopover, placementAtPos } from "./MenuPopover";
import { filterSlashItems, SLASH_ITEMS, type SlashItem } from "./slash-items";

interface Row {
  id: string;
  title: string;
  subtitle?: string;
  shortcut?: string;
  /** Drawn before this row when it opens a new group (`editor.md` §2). */
  separatorBefore?: boolean;
  run: (view: EditorView, context: EditorContext) => void;
}

function slashRows(query: string, allowed?: readonly string[]): Row[] {
  const items = allowed ? SLASH_ITEMS.filter((item) => allowed.includes(item.id)) : SLASH_ITEMS;
  const filtered = filterSlashItems(query, items);
  return filtered.map((item: SlashItem, index) => ({
    id: item.id,
    title: item.title,
    ...(item.shortcut === undefined ? {} : { shortcut: item.shortcut }),
    // A separator is never first, last, or adjacent to another one after
    // filtering, so it is drawn from the surviving neighbours.
    separatorBefore: index > 0 && filtered[index - 1]?.group !== item.group,
    run: item.run,
  }));
}

function emojiRows(query: string): Row[] {
  return searchEmoji(query, 12).map((entry) => ({
    id: entry.name,
    title: `${entry.character}  :${entry.name}:`,
    run: (view) => {
      insertEmoji(entry.name)(view.state, view.dispatch, view);
    },
  }));
}

function mentionRows(results: MentionSearchResult[]): Row[] {
  return results.map((result) => ({
    id: `${result.type}-${result.modelId}`,
    title: result.label,
    ...(result.subtitle === undefined ? {} : { subtitle: result.subtitle }),
    run: (view) => {
      insertMention(result)(view.state, view.dispatch, view);
    },
  }));
}

export type SuggestionKeyHandler = ((event: KeyboardEvent) => boolean) | null;

export interface SuggestionMenuProps {
  view: EditorView;
  state: EditorState;
  context: EditorContext;
  /** Hands the open menu the arrow keys and Enter while it is showing. */
  registerKeys: (handler: SuggestionKeyHandler) => void;
  /** The reduced editor offers a subset of the block menu (§1). */
  allowedItems?: readonly string[];
}

interface SearchState {
  /** `null` until a search has come back, so nothing is ever shown stale. */
  query: string | null;
  scope: string;
  results: MentionSearchResult[];
}

const EMPTY_RESULTS: MentionSearchResult[] = [];
const NO_SEARCH: SearchState = { query: null, scope: "", results: EMPTY_RESULTS };

/**
 * The `/`, `:`, `@` and `[[` menus. They share one trigger plugin and one list
 * widget: filterable by title and keywords, keyboard navigable, Escape to
 * close, and closed after an insert.
 */
export function SuggestionMenu({
  view,
  state,
  context,
  registerKeys,
  allowedItems,
}: SuggestionMenuProps): ReactNode {
  const suggestion = activeSuggestion(state);
  const [search, setSearch] = useState<SearchState>(NO_SEARCH);
  // The highlighted row is keyed by the query it belongs to, so a new query
  // starts at the top without a state write during rendering.
  const [highlight, setHighlight] = useState({ key: "", index: 0 });
  const type = suggestion?.type ?? "";
  const query = suggestion?.query ?? "";
  const needsSearch = type === "mention" || type === "document";
  const scope = type === "document" ? "document" : "all";

  useEffect(() => {
    const handler = context.searchMentions;
    if (!needsSearch || !handler) return;
    let cancelled = false;
    void handler(query, scope === "document" ? "document" : "all")
      .then((found) => {
        if (!cancelled) setSearch({ query, scope, results: found });
      })
      .catch(() => {
        if (!cancelled) setSearch({ query, scope, results: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [context.searchMentions, needsSearch, query, scope]);

  const fresh = search.query === query && search.scope === scope;
  const searching = needsSearch && !fresh && context.searchMentions !== null;
  const results = useMemo(() => (fresh ? search.results : EMPTY_RESULTS), [fresh, search.results]);

  const rows = useMemo<Row[]>(() => {
    if (!suggestion) return [];
    if (suggestion.type === "slash") return slashRows(suggestion.query, allowedItems);
    if (suggestion.type === "emoji") return emojiRows(suggestion.query);
    const found = mentionRows(results);
    // §2: a name that matches nothing offers to create the document.
    if (found.length === 0 && suggestion.query.trim() !== "" && context.createDocument) {
      const title = suggestion.query.trim();
      const create = context.createDocument;
      return [
        {
          id: "create-document",
          title: `Create a new document “${title}”`,
          run: (target) => {
            void create(title).then((mention) => {
              if (mention) insertMention(mention)(target.state, target.dispatch, target);
            });
          },
        },
      ];
    }
    return found;
  }, [allowedItems, context.createDocument, results, suggestion]);

  const key = `${type}:${query}:${String(rows.length)}`;
  const active = highlight.key === key ? highlight.index : 0;

  const select = useCallback(
    (row: Row | undefined, current: SuggestionState) => {
      if (!row) return;
      replaceSuggestion(view, current, (target) => {
        row.run(target, context);
      });
    },
    [context, view],
  );

  useEffect(() => {
    if (!suggestion || rows.length === 0) {
      registerKeys(null);
      return;
    }
    registerKeys((event: KeyboardEvent): boolean => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        const step = event.key === "ArrowDown" ? 1 : -1;
        setHighlight({ key, index: (active + step + rows.length) % rows.length });
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        select(rows[active], suggestion);
        return true;
      }
      return false;
    });
    return () => {
      registerKeys(null);
    };
  }, [active, key, registerKeys, rows, select, suggestion]);

  if (!suggestion) return null;
  if (rows.length === 0) {
    return searching ? (
      <MenuPopover
        placement={placementAtPos(view, suggestion.from)}
        role="listbox"
        label="Searching"
        testId="suggestion-menu"
      >
        <p className="wiki-menu__empty">Searching…</p>
      </MenuPopover>
    ) : null;
  }

  return (
    <MenuPopover
      placement={placementAtPos(view, suggestion.from)}
      role="listbox"
      label={
        suggestion.type === "slash"
          ? "Block menu"
          : suggestion.type === "emoji"
            ? "Emoji"
            : "Mentions"
      }
      className="wiki-menu--list"
      testId="suggestion-menu"
      onDismiss={() => {
        dismissSuggestion(view);
      }}
    >
      <ul className="wiki-menu__list">
        {rows.map((row, index) => (
          <li
            key={row.id}
            className={row.separatorBefore === true ? "wiki-menu__group" : undefined}
          >
            <button
              type="button"
              role="option"
              aria-selected={index === active}
              className={cx("wiki-menu__item", index === active && "wiki-menu__item--active")}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                select(row, suggestion);
              }}
            >
              <span className="wiki-menu__item-title">{row.title}</span>
              {row.subtitle !== undefined && (
                <span className="wiki-menu__item-subtitle">{row.subtitle}</span>
              )}
              {row.shortcut !== undefined && (
                <span className="wiki-menu__item-shortcut">{row.shortcut}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </MenuPopover>
  );
}
